<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import type GTextInput from '@consumer/components/GTextInput.vue'
import { formatCurrency } from '@consumer/utils/currency'
import { preventNonNumericInput } from '@consumer/utils/number'
import newMiscCustomAmountIcon from '@consumer/assets/icons/new-misc/custom-amount.svg?svgicon'

const props = defineProps<{
  minimumAmount: number
  maximumAmount: number
}>()

const { minimumAmount, maximumAmount } = toRefs(props)

let customAmount = _ref<string | null>(null)
const customAmountNumeric = _computed(() => (
  customAmount.value && parseInt(customAmount.value.replace('$', ''))
))

const emit = defineEmits<{ save: [amount: number] }>()

const customAmountInputRef = _ref<InstanceType<typeof GTextInput>>()

const setCustomAmount = (event: InputEvent) => {
  const target = event.target as HTMLInputElement
  const value = target.value ? parseInt(target.value.replace('$', '')) : null
  if (value) {
    if (value > maximumAmount.value) {
      target.value = `$${maximumAmount.value}`
      customAmount.value = `$${maximumAmount.value}`
    }
    else {
      customAmount.value = `$${value.toString()}`
    }
  }
}

const canSave = computed(() => {
  return customAmountNumeric.value
  && customAmountNumeric.value >= minimumAmount.value
  && customAmountNumeric.value <= maximumAmount.value
})

const onSave = () => {
  if (customAmountNumeric.value) {
    emit('save', customAmountNumeric.value)
  }
}

defineExpose({
  focus: () => customAmountInputRef.value?.focus(),
})
</script>

<template>
  <form class="mt-5 lg:mt-3" @submit.prevent="onSave">
    <GIcon
      :svg="newMiscCustomAmountIcon"
      class="my-10 lg:hidden"
      style="height: 108px; width: 170px;"
    />

    <div class="text-xl font-semibold mb-4">
      Your gift can range from ${{ minimumAmount }} to ${{ maximumAmount }} - pick what feels right!
    </div>

    <GTextInput
      ref="customAmountInputRef"
      v-model="customAmount"
      v-mask="`$####`"
      name="custom-amount-input"
      label="Gift amount"
      class="text-center custom-amount-input !text-3xl !font-semibold !text-primary-base !py-2"
      min="5"
      max="1000"
      step="5"
      placeholderAlignment="center"
      wrapperClass="flex-grow"
      :class="customAmount && '!border-primary-base !bg-primary-pale'"
      inputmode="numeric"
      @keypress="preventNonNumericInput"
      @input="setCustomAmount"
    />

    <GButton
      name="save-amount-button"
      size="large"
      primary
      class="w-full mt-5"
      :disabled="!canSave"
      type="submit"
    >
      <template v-if="customAmountNumeric && customAmountNumeric >= minimumAmount">
        Save
      </template>
      <template v-else>
        Minimum {{ formatCurrency(minimumAmount) }}
      </template>
    </GButton>
  </form>
</template>
